import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { AnimatedThemeToggler } from './ui/animated-theme-toggler';

const navItems = [
  { href: '#home', label: 'Home' },
  { href: '#projects', label: 'Work' },
  { href: '#experience', label: 'Experience' },
  { href: '#skills', label: 'Skills' },
  { href: '#contact', label: 'Contact' },
];

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-paper dark:bg-noir border-b-[3px] border-ink dark:border-cream">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="#home" onClick={closeMenu} className="flex items-center gap-3 group">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-sun brutal-border font-display text-lg font-extrabold text-ink -rotate-3 group-hover:rotate-0 transition-transform duration-200">
              TA
            </span>
            <span className="font-display text-lg font-bold tracking-tight text-ink dark:text-cream hidden sm:block">
              Tushar Anand
            </span>
          </a>

          <div className="hidden md:flex items-center gap-2">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="px-4 py-2 rounded-full text-sm font-bold text-ink dark:text-cream hover:bg-sun dark:hover:bg-sun dark:hover:text-ink brutal-border border-transparent hover:border-ink dark:hover:border-ink transition-colors"
              >
                {item.label}
              </a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <AnimatedThemeToggler className="flex h-11 w-11 items-center justify-center rounded-xl bg-cream dark:bg-card text-ink dark:text-cream brutal-border brutal-hover [&_svg]:w-5 [&_svg]:h-5" />
            <a
              href="#contact"
              className="inline-flex items-center px-5 py-2.5 rounded-full bg-mint text-ink font-display font-bold text-sm brutal-border brutal-hover"
            >
              Let's talk
            </a>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <AnimatedThemeToggler className="flex h-10 w-10 items-center justify-center rounded-xl bg-cream dark:bg-card text-ink dark:text-cream brutal-border-thin [&_svg]:w-5 [&_svg]:h-5" />
            <button
              onClick={() => setIsMenuOpen((open) => !open)}
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-sun text-ink brutal-border-thin"
              aria-label="Toggle navigation menu"
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="md:hidden overflow-hidden border-t-[3px] border-ink dark:border-cream bg-paper dark:bg-noir"
          >
            <div className="px-4 py-4 space-y-2">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={closeMenu}
                  className="block px-4 py-3 rounded-xl text-base font-bold text-ink dark:text-cream bg-cream dark:bg-card brutal-border-thin"
                >
                  {item.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
